import { toTypedRxJsonSchema } from 'rxdb';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';

export const transactionSchemaLiteral = {
  title: 'transaction',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    type: { type: 'string', enum: ['income', 'expense'], maxLength: 20 },
    subtype: { type: 'string', maxLength: 50 },
    date: { type: 'string', maxLength: 50 },
    operational_date: { type: 'string', maxLength: 50 },
    period: { type: 'string', maxLength: 7 },
    reference: { type: ['string', 'null'], maxLength: 100 },
    description: { type: ['string', 'null'], maxLength: 500 },
    
    // Totals in Base Currency
    total_amount: { type: 'number' },
    paid_amount: { type: 'number' },
    pending_balance: { type: 'number' },
    base_currency: { type: 'string', maxLength: 10 },
    
    // Payment Status
    payment_status: { type: 'string', enum: ['paid', 'partial', 'pending'], maxLength: 20 },
    status: { type: 'string', enum: ['draft', 'completed', 'pending', 'voided'], maxLength: 20 },
    void_reason: { type: ['string', 'null'], maxLength: 500 },
    voided_by_user_id: { type: ['string', 'null'], maxLength: 100 },
    
    // Relationships & Branch Separation
    branch_id: { type: 'string', maxLength: 100 },
    customer_id: { type: ['string', 'null'], maxLength: 100 },
    customer_name: { type: ['string', 'null'], maxLength: 255 },
    notes: { type: ['string', 'null'] },
    reconciliation: { type: ['string', 'null'], maxLength: 50 },
    
    // Splits: Item / Gasto snapshot breakdown (Base Currency only)
    items: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', maxLength: 100 },
          type: { type: 'string', enum: ['service', 'product', 'expense', 'package'] },
          concept: { type: 'string', maxLength: 255 },
          quantity: { type: 'number' },
          unit_price: { type: 'number' },
          subtotal: { type: 'number' },
          discount: { type: 'number' },
          net_amount: { type: 'number' },
          category_id: { type: 'string', maxLength: 100 },
          service_id: { type: ['string', 'null'], maxLength: 100 },
          operator_id: { type: ['string', 'null'], maxLength: 100 }, // References staff.id
          commission_percentage: { type: 'number' },
          commission_amount: { type: 'number' }
        },
        required: [
          'id', 'type', 'concept', 'quantity', 'unit_price', 
          'subtotal', 'discount', 'net_amount', 'category_id'
        ]
      }
    },
    
    // Splits: Payments list (Multi-currency physical wallets)
    payments: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', maxLength: 100 },
          payment_method_id: { type: 'string', maxLength: 100 },
          currency: { type: 'string', maxLength: 10 },
          exchange_rate: { type: 'number' },
          received_amount: { type: 'number' }, // En la currency del wallet
          base_equivalent: { type: 'number' }, // En la currency base
          reference: { type: ['string', 'null'], maxLength: 100 },
          bank_movement_id: { type: ['string', 'null'], maxLength: 100 }
        },
        required: ['id', 'payment_method_id', 'currency', 'exchange_rate', 'received_amount', 'base_equivalent']
      }
    },
    
    metadata: { type: 'object' },
    
    // Audit & Control
    registered_by: { type: ['string', 'null'], maxLength: 100 },
    created_at: { type: 'string', maxLength: 50 },
    updated_at: { type: 'string', maxLength: 50 },
    is_deleted: { type: 'boolean' },
    last_modified_by: { type: ['string', 'null'], maxLength: 100 }
  },
  required: [
    'id', 'type', 'subtype', 'date', 'operational_date', 'period', 'total_amount', 
    'paid_amount', 'pending_balance', 'payment_status', 'base_currency', 'branch_id', 
    'items', 'payments', 'metadata', 'updated_at', 'is_deleted', 'status'
  ],
  indexes: ['is_deleted', 'date', 'period', 'branch_id', 'type', 'subtype', 'updated_at', 'status']
} as const;

const schemaTyped = toTypedRxJsonSchema(transactionSchemaLiteral);
export type TransactionDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>;
export const transactionSchema: RxJsonSchema<TransactionDocType> = transactionSchemaLiteral;
